import React from 'react';
import { FiCheckCircle, FiTrendingUp, FiTarget, FiDollarSign, FiUsers, FiBarChart, FiGlobe, FiAward, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const BingAdsPage = () => {
  useDocumentTitle(
    'Performance-Driven Bing Ads | Handled by a Skilled Bing Ads Expert in India',
    'A bing ads agency in india that simplifies your ads and improves returns. You get an advertising bing strategy built around clarity, budget control, and strong targeting.'
  );

  const features = [
    {
      icon: FiTarget,
      title: "Campaign Setup & Structure",
      description: "We create search and shopping campaigns with the right keywords, match types, locations and devices so your budget goes to the right people."
    },
    {
      icon: FiBarChart,
      title: "Conversion Tracking & Analytics",
      description: "We set up or fix tracking tags so you can see calls, form fills, purchases and other actions clearly inside your Bing account and analytics."
    },
    {
      icon: FiTrendingUp,
      title: "Ongoing Optimisation & Testing",
      description: "We add and block keywords, change bids, test new ads and adjust targeting to keep improving cost per lead or sale."
    },
    {
      icon: FiGlobe,
      title: "Google to Bing Migration",
      description: "If you already run Google Ads, we carefully copy your best campaigns into Bing, then fine\u2011tune them for Microsoft's audience and policies."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "₹12,000",
      period: "/month",
      description: "Perfect for small businesses entering Bing Ads",
      features: [
        "Ad Spend: Up to ₹30,000/month",
        "1 Search Campaign",
        "Basic Keyword Research",
        "Ad Copy Creation (5 ads)",
        "Monthly Performance Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Growth",
      price: "₹25,000",
      period: "/month",
      description: "Ideal for growing businesses scaling Bing presence",
      features: [
        "Ad Spend: Up to ₹1,00,000/month",
        "3 Search Campaigns",
        "Advanced Keyword Research",
        "Ad Copy Creation (15 ads)",
        "LinkedIn Profile Targeting",
        "Shopping Campaign Setup",
        "Bi-weekly Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large businesses needing full Bing Ads management",
      features: [
        "Unlimited Ad Spend",
        "Unlimited Campaigns",
        "Complete Account Management",
        "Microsoft Audience Network",
        "Advanced LinkedIn Targeting",
        "A/B Testing & Optimization",
        "Weekly Performance Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "Performance‑first mindset",
      description: "Mirani works like a focused bing ads agency in india: every decision is based on data, ROI and long‑term growth, not just impressions."
    },
    {
      title: "Smooth Google + Bing combo",
      description: "We build Bing as a natural extension of your Google Ads, so advertising bing feels simple and connected instead of like a separate, confusing channel."
    },
    {
      title: "Clear communication & full control",
      description: "You always keep ownership of your account and see exactly where your money is going, with honest, easy‑to‑read reports each month."
    }
  ];

  const faqs = [
    {
      question: "Do I really need Bing Ads if I already use Google Ads?",
      answer: "Bing usually has less competition and can offer lower cost per click. It's a smart way to add extra leads and sales from a different but valuable audience."
    },
    {
      question: "How much should I budget for Bing Ads?",
      answer: "You can start smaller than Google to test the waters. Once we see steady results, we scale your spend while closely watching cost per lead or sale."
    },
    {
      question: "Can you move my Google campaigns to Bing?",
      answer: "Yes. We import your best campaigns, adapt them to Bing's settings, and then optimise based on performance data."
    },
    {
      question: "How long until I see results?",
      answer: "Most accounts see useful data and early wins within 2–4 weeks, then stronger performance after a few rounds of testing and tuning."
    },
    {
      question: "Will I get regular updates?",
      answer: "Yes. You receive a clear monthly report and can book calls to review what's working, what's not and what we'll do next."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#00809d] to-[#005a73] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Bing / Microsoft Ads Management
            </h1>
            <div className="space-y-2 mb-6 text-lg">
              <p>Want to test <span className="font-semibold">advertising bing</span> without wasting money on trial and error?</p>
              <p>Looking for a trusted <span className="font-semibold">bing ads agency in india</span> that actually reports profits, not just clicks?</p>
              <p>Get a dedicated <span className="font-semibold">bing ads expert in india</span> to launch, manage and grow your Microsoft Ads.</p>
            </div>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Mirani Marketing helps you reach new customers on Bing, Yahoo and the Microsoft network with clear strategy, clean tracking and simple reports. You get a second high‑intent traffic source, without feeling overwhelmed by another platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#00809d] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                Get a Free Bing Ads Audit
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#00809d] transition-colors inline-block"
              >
                Talk to Our Bing Ads Expert
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is Bing / Microsoft Ads?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                Bing / Microsoft Ads let your business show up when people search on Bing and its partner networks. These users are often older, high‑income and use Microsoft products at work and at home.
              </p>
              <p className="text-lg leading-relaxed">
                While most brands only focus on Google, smart <span className="font-semibold">advertising bing</span> can give you cheaper clicks, extra leads and more visibility where your competitors are not paying attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our Bing Ads Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#00809d] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#00809d]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-[#00809d] text-white shadow-2xl scale-105 border-4 border-[#00809d]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#00809d] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#00809d]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#00809d]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#00809d] hover:bg-gray-100'
                      : 'bg-[#00809d] text-white hover:bg-[#006d87]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for Bing Ads?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#00809d] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#00809d]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#00809d] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#00809d] to-[#005a73] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Reach Bing's Audience?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            If you're exploring advertising bing and want a reliable bing ads agency in india to handle everything for you, Mirani Marketing is ready to help you launch, clean up or scale your Bing / Microsoft Ads.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#00809d] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get a Free Bing Ads Strategy Call
            </a>
            <a
              href="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#00809d] transition-colors inline-block"
            >
              Request Your Bing Ads Audit
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BingAdsPage;
